import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map WeatherAPI condition text to native emoji
const getWeatherEmoji = (label) => {
  if (!label) return '🌤';
  const l = label.toLowerCase();
  if (l.includes('thunder') || l.includes('storm')) return '⛈️';
  if (l.includes('heavy rain') || l.includes('torrential')) return '🌧️';
  if (l.includes('sleet') || l.includes('freezing')) return '🌨️';
  if (l.includes('snow') || l.includes('blizzard')) return '❄️';
  if (l.includes('rain') || l.includes('drizzle') || l.includes('shower')) return '🌦️';
  if (l.includes('fog') || l.includes('mist') || l.includes('haze')) return '🌫️';
  if (l.includes('overcast')) return '☁️';
  if (l.includes('cloudy') || l.includes('cloud')) return '⛅';
  if (l.includes('sunny') || l.includes('clear')) return '☀️';
  return '🌤';
};

export function useWeather() {
  const [forecast, setForecast] = useState(() => {
    try {
      const cached = localStorage.getItem('weatherForecast');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - (parsed._ts || 0) < 1 * 60 * 60 * 1000) {
          return parsed.data || {};
        }
      }
    } catch {}
    return {};
  });

  useEffect(() => {
    axios.get(`${API}/weather/forecast`)
      .then(res => {
        if (res.data?.success && res.data.forecast) {
          setForecast(res.data.forecast);
          try {
            localStorage.setItem('weatherForecast', JSON.stringify({
              data: res.data.forecast,
              _ts: Date.now()
            }));
          } catch {}
        }
      })
      .catch(() => {});
  }, []);

  return forecast;
}

const todayCT = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });

export function WeatherBadge({ dateStr, forecast, compact = false }) {
  if (!forecast || !dateStr) return null;
  const day = forecast[dateStr];
  if (!day) return null;

  const emoji = getWeatherEmoji(day.label);
  const isActual = dateStr < todayCT();
  const kind = isActual ? 'actual' : 'forecast';
  const title = `${isActual ? 'Actual' : 'Forecast'}: ${day.label} — ${day.high}°/${day.low}°F`;
  const tag = isActual ? 'ACTUAL' : 'FCST';
  const box = isActual
    ? 'border border-emerald-500/50 bg-emerald-500/10'
    : 'border border-dashed border-amber-400/50';
  const highCls = isActual ? 'text-emerald-300 not-italic' : 'text-amber-400 italic';
  const tagCls = isActual ? 'text-emerald-400' : 'text-amber-400/80';
  const sz = compact
    ? { emoji: 'text-lg', num: 'text-xs', tag: 'text-[8px]', pad: 'px-1 py-0.5 gap-1' }
    : { emoji: 'text-2xl', num: 'text-sm', tag: 'text-[9px]', pad: 'px-1.5 py-0.5 gap-1.5' };

  return (
    <div
      data-testid={`weather-badge-${dateStr}`}
      data-weather-kind={kind}
      className={`flex items-center flex-shrink-0 rounded-md ${sz.pad} ${box}`}
      title={title}
    >
      <span className={`${sz.emoji} leading-none ${isActual ? '' : 'opacity-80'}`}>{emoji}</span>
      <div className="flex flex-col items-end">
        <span data-testid={`weather-kind-${dateStr}`} className={`${sz.tag} font-bold tracking-wider leading-none ${tagCls}`}>
          {tag}
        </span>
        <span className={`${sz.num} font-bold leading-tight ${highCls}`}>
          {day.high}°
        </span>
        <span className={`${sz.num} text-gray-400 leading-tight ${isActual ? '' : 'italic'}`}>
          {day.low}°
        </span>
      </div>
    </div>
  );
}
